import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import Section from '../components/common/Section';
import Button from '../components/common/Button';

interface PostRow {
  id: number;
  slug: string;
  title: string;
  published: number;
  created_at: string;
  updated_at: string;
}

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: var(--text-color);
  background: var(--background-light);
  border-radius: var(--border-radius);
  overflow: hidden;

  th, td {
    padding: 0.9rem 1rem;
    text-align: left;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  th { font-weight: 600; background: rgba(0,0,0,0.2); }
  tr:last-child td { border-bottom: none; }
  .actions { display: flex; gap: 0.5rem; }
  .pill {
    display: inline-block;
    padding: 0.15rem 0.6rem;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
  }
  .pill.pub { background: rgba(70,180,80,0.2); color: #6bcf6b; }
  .pill.draft { background: rgba(255,180,50,0.2); color: #ffb733; }
`;

const Notice = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-secondary);
`;

const AdminPage: React.FC = () => {
  const nav = useNavigate();
  const [posts, setPosts] = useState<PostRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const res = await fetch('/api/admin/posts');
    if (res.status === 401) {
      nav('/admin/login');
      return;
    }
    if (!res.ok) {
      setError('Could not load posts');
      return;
    }
    const data = await res.json();
    setPosts(data.posts || []);
  }, [nav]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = async () => {
    await fetch('/api/admin/logout', { method: 'POST' });
    nav('/admin/login');
  };

  const remove = async (id: number, title: string) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    const res = await fetch(`/api/admin/posts/${id}`, { method: 'DELETE' });
    if (res.ok) load();
    else alert('Delete failed');
  };

  return (
    <Layout>
      <Section background="light" padding="large">
        <Toolbar>
          <h1 style={{ margin: 0 }}>Posts</h1>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Link to="/admin/new">
              <Button variant="primary">New post</Button>
            </Link>
            <Button variant="outline" onClick={logout}>
              Log out
            </Button>
          </div>
        </Toolbar>

        {error && <Notice>{error}</Notice>}
        {!error && posts === null && <Notice>Loading…</Notice>}
        {!error && posts !== null && posts.length === 0 && (
          <Notice>No posts yet. Click "New post" to create one.</Notice>
        )}
        {!error && posts !== null && posts.length > 0 && (
          <Table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{p.title}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      /blog/{p.slug}
                    </div>
                  </td>
                  <td>
                    <span className={`pill ${p.published ? 'pub' : 'draft'}`}>
                      {p.published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    {new Date(p.updated_at.replace(' ', 'T') + 'Z').toLocaleString()}
                  </td>
                  <td>
                    <div className="actions">
                      <Link to={`/admin/edit/${p.id}`}>
                        <Button variant="outline" size="small">Edit</Button>
                      </Link>
                      <Button variant="outline" size="small" onClick={() => remove(p.id, p.title)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </Layout>
  );
};

export default AdminPage;
